/* eslint-disable no-unsafe-optional-chaining */
/* eslint-disable no-restricted-globals */
import { isPast, isValid, lightFormat, parse } from 'date-fns';
import Decimal from 'decimal.js';
import { isEmptyOrUndefined } from '../../../utils/common/validators';
import {
	FIELD_IDENTIFIER,
	ORDER_FORM_ERROR,
	ORDER_TYPES,
	SIP_FIELD_IDENTIFIERS,
	TRANSACTION_TYPE,
	VALIDITY_TYPE,
} from '../../../constants/equity/orderForm/oneTime';
import orderFormService from './orderFormService';
import { ltpFeedService } from '../ltpFeedService';
import { FREQUENCY_MAPPER } from '../../../constants/equity/orderForm/orderFormBase';
import orderScripService from './orderScripService';
import stocksPortfolioService from '../stocksPortfolio/stocksPortfolioService';
import { EQUITY_FO_INSTRUMENT } from '../../../constants/scripDetail';

function getDprErrorMessage(field, price, dprRange = '') {
	let formattedDpr = '';
	const isDprRangeEmpty = isEmptyOrUndefined(dprRange);
	if (!isDprRangeEmpty) {
		const [min, max] = dprRange?.split('-');
		formattedDpr = `${min} - ${max}`;
	}

	const preffixText =
		field === FIELD_IDENTIFIER.price
			? 'Limit price'
			: 'Stoploss trigger price';
	const suffixText = isDprRangeEmpty ? '' : ` of ${formattedDpr}`;

	const message = `${preffixText} (${price}) is outside the daily price range${suffixText}`;
	return message;
}

function checkValueInDprRange(amt, priceRange) {
	try {
		const min = priceRange?.split('-')[0];
		const max = priceRange?.split('-')[1];
		const inDprRange =
			Number(amt) >= Number(min) && Number(amt) <= Number(max);
		return inDprRange;
	} catch (error) {
		return false;
	}
}

function checkIsDivisible(num1, num2) {
	if (Number(num1) === 0 && Number(num2) === 0) {
		return { isDivisible: false, quotient: 1 };
	}

	try {
		const numerator = new Decimal(num1);
		const denominator = new Decimal(num2);
		const val = numerator.dividedBy(denominator);
		const isDivisible = val.isInteger();
		return { isDivisible, quotient: val };
	} catch (error) {
		return { isDivisible: false, quotient: 1 };
	}
}

function validateOrderForm({
	formData = {},
	transactionType = '',
	productType = {},
	ltp = '',
	dprRange,
	priceTick,
	lotSize = 1,
	exchange = '',
	availableQuantity,
}) {
	// p2. Qty field not filled in.
	if (isEmptyOrUndefined(formData?.quantity)) {
		const errorMessage = ORDER_FORM_ERROR.validQty;
		return {
			isError: true,
			errorMessage,
			field: FIELD_IDENTIFIER.quantity,
		};
	}

	// 	p2. Qty field value = 0
	if (Number(formData.quantity) === 0) {
		const errorMessage = ORDER_FORM_ERROR.zeroQty;
		return {
			isError: true,
			errorMessage,
			field: FIELD_IDENTIFIER.quantity,
		};
	}

	// p3. Price field not filled in
	const isNotMarketPriceOrder =
		formData?.orderType !== ORDER_TYPES.market.value &&
		formData?.orderType !== ORDER_TYPES.stoplossMarket.value;
	// const isLimitOrStopLoss = [ORDER_TYPES.limit.value, ORDER_TYPES.stoploss.value].includes(formData?.orderType);

	if (isNotMarketPriceOrder && isEmptyOrUndefined(formData?.price)) {
		const errorMessage = ORDER_FORM_ERROR.validPrice;
		return { isError: true, errorMessage, field: FIELD_IDENTIFIER.price };
	}

	// p3. price check
	if (isNotMarketPriceOrder && Number(formData.price) === 0) {
		const errorMessage = ORDER_FORM_ERROR.zeroPrice;
		return { isError: true, errorMessage, field: FIELD_IDENTIFIER.price };
	}

	// p4. Qty should be a multiple of lot size for the scrip
	const isLotSizeApplicable = orderFormService.isLotSizeApplicable(exchange);
	const { isDivisible, quotient } = checkIsDivisible(
		formData?.quantity,
		lotSize,
	);
	const lotSizeNotDivisible = !isDivisible;
	const nextValidQty = Math.ceil(quotient) * lotSize;
	if (isLotSizeApplicable && lotSizeNotDivisible) {
		const errorMessage = `${ORDER_FORM_ERROR.qtyMultipleOfLot} (${lotSize}). Try ${nextValidQty}`;
		return {
			isError: true,
			errorMessage,
			field: FIELD_IDENTIFIER.quantity,
		};
	}

	// p5. Limit Price should be a multiple of tick size 0.05.
	const { isDivisible: priceTicDivisible } = checkIsDivisible(
		formData?.price,
		priceTick,
	);
	if (isNotMarketPriceOrder && !priceTicDivisible) {
		const errorMessage = `${ORDER_FORM_ERROR.limitPriceTick} ${priceTick}`;
		return { isError: true, errorMessage, field: FIELD_IDENTIFIER.price };
	}

	// Price input should be within DPR
	const isLimitPriceValid = checkValueInDprRange(formData?.price, dprRange);
	if (isNotMarketPriceOrder && !isLimitPriceValid) {
		const errorMessage = ORDER_FORM_ERROR.limitPriceDpr;
		return {
			isError: true,
			errorMessage,
			field: FIELD_IDENTIFIER.price,
			showDprSheet: true,
		};
	}

	// Stoploss limit
	const shouldCheckStoplossLimit =
		formData.orderType === ORDER_TYPES.stoploss.value;

	if (shouldCheckStoplossLimit) {
		const trigger = formData.stoplossTrigger;

		// p6.1. Trigger price should be in multiples of Price tick
		const { isDivisible: isTriggerPriceDivisible } = checkIsDivisible(
			trigger,
			priceTick,
		);
		if (!isTriggerPriceDivisible) {
			const errorMessage = `${ORDER_FORM_ERROR.triggerMultipleOfPriceTick} ${priceTick}`;
			return {
				isError: true,
				errorMessage,
				field: FIELD_IDENTIFIER.stoploss,
			};
		}

		// for buy
		if (transactionType === TRANSACTION_TYPE.BUY) {
			// p6.1 Trigger Price should be>=LTP  // need to verify
			if (trigger < ltp) {
				const errorMessage = ORDER_FORM_ERROR.triggerGreaterThanLtp;
				return {
					isError: true,
					errorMessage,
					field: FIELD_IDENTIFIER.stoploss,
				};
			}
			// p6.2 Trigger Price<=Limit Price
			if (trigger > formData?.price) {
				const errorMessage = ORDER_FORM_ERROR.triggerLowerThanLimit;
				return {
					isError: true,
					errorMessage,
					field: FIELD_IDENTIFIER.stoploss,
				};
			}
		} else {
			// for sell
			// p6.1 Trigger Price should be<=LTP
			if (trigger > ltp) {
				const errorMessage = ORDER_FORM_ERROR.triggerLowerThanLtp;
				return {
					isError: true,
					errorMessage,
					field: FIELD_IDENTIFIER.stoploss,
				};
			}

			// p6.2 Trigger Price>=Limit Price
			if (trigger < formData.price) {
				const errorMessage = ORDER_FORM_ERROR.triggerGreaterThanLmit;
				return {
					isError: true,
					errorMessage,
					field: FIELD_IDENTIFIER.stoploss,
				};
			}
		}

		// Trigger Price, Limit Price should be within DPR
		const isTriggerPriceValid = checkValueInDprRange(trigger, dprRange);
		if (!isTriggerPriceValid) {
			const errorMessage = ORDER_FORM_ERROR.triggerInDpr;
			return {
				isError: true,
				errorMessage,
				field: FIELD_IDENTIFIER.stoploss,
				showDprSheet: true,
			};
		}
	}

	// p6 Stoploss Market
	const shouldCheckStoplossMarket =
		formData.orderType === ORDER_TYPES.stoplossMarket.value;

	if (shouldCheckStoplossMarket) {
		const trigger = formData.stoplossTrigger;

		// p6.1. Trigger price should be in multiples of Price tick
		const { isDivisible: isTriggerPriceDivisible } = checkIsDivisible(
			trigger,
			priceTick,
		);
		if (!isTriggerPriceDivisible) {
			const errorMessage = `${ORDER_FORM_ERROR.triggerMultipleOfPriceTick} ${priceTick}`;
			return {
				isError: true,
				errorMessage,
				field: FIELD_IDENTIFIER.stoploss,
			};
		}

		// for buy
		if (transactionType === TRANSACTION_TYPE.BUY) {
			// p6.2 Trigger Price should be>=LTP
			if (trigger < ltp) {
				const errorMessage = ORDER_FORM_ERROR.triggerGreaterThanLtp;
				return {
					isError: true,
					errorMessage,
					field: FIELD_IDENTIFIER.stoploss,
				};
			}
		} else {
			// for sell
			// p6.2 Trigger Price should be<=LTP
			if (trigger > ltp) {
				const errorMessage = ORDER_FORM_ERROR.triggerLowerThanLtp;
				return {
					isError: true,
					errorMessage,
					field: FIELD_IDENTIFIER.stoploss,
				};
			}
		}

		// p6.4 Trigger Price, Limit Price should be within DPR
		const isTriggerPriceValid = checkValueInDprRange(trigger, dprRange);
		if (!isTriggerPriceValid) {
			const errorMessage = ORDER_FORM_ERROR.triggerInDpr;
			return {
				isError: true,
				errorMessage,
				field: FIELD_IDENTIFIER.stoploss,
				showDprSheet: true,
			};
		}
	}

	// p7 disclosed Qty
	const shouldCheckDisclosedQty =
		[VALIDITY_TYPE.DAY.value, VALIDITY_TYPE.EOTODY.value].includes(
			formData?.validity,
		) &&
		!isEmptyOrUndefined(formData?.disclosedQuantity) &&
		Number(formData?.disclosedQuantity) > 0;

	if (shouldCheckDisclosedQty && formData?.exchange !== 'NSE_FO') {
		const minDisclosed = Math.ceil(formData.quantity / 10);

		const isDisclosedQtyMaxError =
			Number(formData.disclosedQuantity) > Number(formData.quantity);
		if (isDisclosedQtyMaxError) {
			// p7. DQ<= Order qty
			const errorMessage = ORDER_FORM_ERROR.disclosedQtyGratedThanOrder;
			return {
				isError: true,
				errorMessage,
				field: FIELD_IDENTIFIER.disclosedQuantity,
			};
		}

		const isDisclosedQtyMinError =
			formData.disclosedQuantity < minDisclosed;
		if (isDisclosedQtyMinError) {
			// p7. DQ>=10% of order qty
			const errorMessage = `Disclosed qty should at least be 10% of order qty (${minDisclosed})`;
			return {
				isError: true,
				errorMessage,
				field: FIELD_IDENTIFIER.disclosedQuantity,
			};
		}
	}

	const isQuantityGreaterThanAvailable = Number(formData.quantity) > Number(availableQuantity);
	const isDeliverySellOrder = orderFormService.isDeliverySellOrder(transactionType, productType?.id);
	const isAvailableQuantityValid = isDeliverySellOrder && availableQuantity !== '-' && isQuantityGreaterThanAvailable;

	if (isAvailableQuantityValid) {
		return { isError: true, availableQtyError: true };
	}

	return { isError: false, errorMessage: '' };
}

function validateSipOrderForm({ formData = {} }) {
	if (isEmptyOrUndefined(formData?.quantity?.toString())) {
		const errorMessage = 'Enter a valid quantity';
		return {
			isError: true,
			errorMessage,
			field: SIP_FIELD_IDENTIFIERS.quantity,
		};
	}

	if (formData.quantity?.toString() === '0') {
		const errorMessage = 'Quantity cannot be 0';
		return {
			isError: true,
			errorMessage,
			field: SIP_FIELD_IDENTIFIERS.quantity,
		};
	}

	if (formData?.showPriceCap && isEmptyOrUndefined(formData.priceCap)) {
		const errorMessage = 'Enter a valid price cap';
		return {
			isError: true,
			errorMessage,
			field: SIP_FIELD_IDENTIFIERS.priceCap,
		};
	}

	if (formData?.showPriceCap && Number(formData.priceCap) === 0) {
		const errorMessage = 'Price cap cannot be 0';
		return {
			isError: true,
			errorMessage,
			field: SIP_FIELD_IDENTIFIERS.priceCap,
		};
	}

	if (isEmptyOrUndefined(formData?.noOfInstallments?.toString())) {
		const errorMessage = 'No. of installments cannot be empty';
		return {
			isError: true,
			errorMessage,
			field: SIP_FIELD_IDENTIFIERS.noOfInstallments,
		};
	}

	if (formData.noOfInstallments?.toString() === '0') {
		const errorMessage = 'No. of installments cannot be 0';
		return {
			isError: true,
			errorMessage,
			field: SIP_FIELD_IDENTIFIERS.noOfInstallments,
		};
	}

	if (
		parseInt(formData.noOfInstallments, 10) < 6 ||
		parseInt(formData.noOfInstallments, 10) > 52
	) {
		const errorMessage = 'No. of installments should be in range 6 - 52';
		return {
			isError: true,
			errorMessage,
			field: SIP_FIELD_IDENTIFIERS.noOfInstallments,
		};
	}

	const startDate = parse(formData?.startDate, 'dd/MM/yyyy', new Date()); // parse date
	const isPastDate = isPast(startDate); // check if date is past

	if (
		isEmptyOrUndefined(formData?.startDate?.toString()) ||
		!isValid(startDate)
	) {
		const errorMessage = 'Enter a valid Start date';
		return {
			isError: true,
			errorMessage,
			field: SIP_FIELD_IDENTIFIERS.startDate,
		};
	}

	if (isPastDate) {
		const errorMessage = 'Start date should be a future date';
		return {
			isError: true,
			errorMessage,
			field: SIP_FIELD_IDENTIFIERS.startDate,
		};
	}

	return { isError: false, errorMessage: '', field: '' };
}

function getOrderPlaceDisplayMessage(
	orderType,
	instrument,
	scripName,
	transactionType,
	isModifyOrder,
	quantity,
	price,
	productType,
	exchange,
) {
	let displayMessage = '';
	const isMarketOrStopLossMarketOrder =
		orderFormService.isMarketOrStopLossMarketOrder(orderType);

	let transactionPrice = '';
	if (!isMarketOrStopLossMarketOrder) {
		const supportedDecimals =
			ltpFeedService.getSupportedDecimals(instrument);
		transactionPrice = !isNaN(price)
			? Number(price)?.toFixed(supportedDecimals)
			: '-';
	}

	const endText = isModifyOrder ? 'modified' : 'submitted';
	const displayTitle = isModifyOrder
		? 'Modify request submitted'
		: 'Order submitted';

	const txnPrice = isMarketOrStopLossMarketOrder
		? 'Market price'
		: transactionPrice;
	if (isModifyOrder) {
		displayMessage = `Order modification request for ${scripName} order for ${quantity} qty at ${txnPrice} is submitted`;
	} else {
		displayMessage = `${transactionType} order for ${quantity} qty of ${scripName} at ${txnPrice} is ${endText}`;
	}

	const { infoText, autoRedirect } = stocksPortfolioService.deliveryT1InfoConfig(productType, transactionType, exchange);

	return { displayTitle, displayMessage, infoText, autoRedirect };
}

function getSipOrderPlaceDisplayMessage(
	isModifySip = false,
	quantity = '',
	scripName = '',
	frequencyType = '',
	capPrice = '',
	showPriceCap = false,
) {
	const endText = isModifySip ? 'modified' : 'placed';
	const displayTitle = isModifySip
		? 'Modify request submitted'
		: 'Order submitted';

	let displayMessage = '';

	if (isModifySip) {
		const txnPrice = showPriceCap ? capPrice : 'Market price';
		displayMessage = `Order modification request for ${scripName} order for ${quantity} qty at ${txnPrice} is submitted`;
	} else {
		displayMessage = `${FREQUENCY_MAPPER[frequencyType]?.title} SIP for ${quantity} qty of ${scripName} is ${endText}`;
	}

	return { displayTitle, displayMessage };
}

function isAddFundsRequired(marginInfo = {}) {
	const requiredMargin = marginInfo?.requiredMargin;
	const isRequiredThanAvailable = requiredMargin > marginInfo?.availableMargin;
	return requiredMargin !== '-' && Number(requiredMargin) !== 0 && isRequiredThanAvailable;
}

function isAsmGsmRequired(asm_gsm) {
	if (isEmptyOrUndefined(asm_gsm)) {
		return false;
	}
	return Number(asm_gsm) !== 0 && asm_gsm !== null;
}

function formatSipStartDate(startDate) {
	let date = startDate.split(' ')[0];

	const day = date.split('-')[2];
	const month = date.split('-')[1].substring(0, 3);
	const year = date.split('-')[0];
	const formatedDate = `${year}-${month}-${day}`;
	date = parse(formatedDate, 'yyyyy-MMM-dd', new Date());
	date = lightFormat(date, 'dd/MM/yyyy');
	return date;
}

function checkIsMinimumQuantity(qty, lotSize, instrument) {
	const isCurrencyScrip =
		orderScripService.isCurrencyDerivatesScrip(instrument);
	const minimumLotSize = isCurrencyScrip ? 1 : lotSize;
	const isMinQty = qty < minimumLotSize;
	return isMinQty;
}
const FO_INSTRUMENT = EQUITY_FO_INSTRUMENT;
const isFOInstrument = (instrument) => {
	return FO_INSTRUMENT.includes(instrument);
};
const isFoBanRequired = (foBan, instrument) => {
	if (isEmptyOrUndefined(foBan)) {
		return false;
	}

	return Number(foBan) !== 0 && foBan !== null && isFOInstrument(instrument);
};

const orderFormValidationService = {
	validateOrderForm,
	validateSipOrderForm,
	getDprErrorMessage,
	getOrderPlaceDisplayMessage,
	getSipOrderPlaceDisplayMessage,
	isAddFundsRequired,
	isAsmGsmRequired,
	checkValueInDprRange,
	checkIsDivisible,
	formatSipStartDate,
	checkIsMinimumQuantity,
	isFoBanRequired,
};

export default orderFormValidationService;
